/* Hallmark · page: invoice-validation · genre: modern-minimal
 * theme: project system (core-frontend DESIGN.md) — Katalog-Rotation gilt nicht
 * audit 2026-10-03: 2 critical · 2 major · 4 minor, alle behoben
 * redesign 2026-10-03: Werkzeug tritt nach Gebrauch zurueck, Ergebnis fuehrt
 * rueckbau 2026-10-03: eigener Pruefpfad raus, Seite liest ueber /parse
 * Muster: UG §2 Unified List in Card · UG §7 Badge · LogoUpload-Zustandswechsel
 * pre-emit critique: P5 H5 E4 S5 R5 V4
 */
/* Hallmark · macrostructure: Single Column Report (app) · tone: technical · theme: console-system · genre: modern-minimal */
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xhubio-saas/core-frontend/ui-components'
import {
  ApiRequestInspector,
  type CatalogCountry,
  CountrySelector,
  findCatalogCountry,
  type RestApiCallState,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { FileUp } from 'lucide-react'
import { type DragEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type Detection,
  documentIdentifier,
  formatDisplayName,
  formatProfile,
  groupFindings,
  type ValidationResult,
} from '../../api/invoiceValidationClient'
import { ContentBlock } from './validation/ContentBlock'
import { FindingsReport } from './validation/FindingsReport'
import { outcomeOf, pluralCategory, VerdictHeader } from './validation/VerdictHeader'
import { Workbench, type WorkbenchPanel } from './validation/Workbench'

/**
 * Validation — eine fertige E-Rechnung pruefen, in zwei Schritten.
 *
 * 🔴 **Erst `/parse`, dann `/validate`** — zwei vorhandene Endpunkte
 * hintereinander, keiner eigens gebaut. Schritt 1 sagt, WAS das Dokument ist
 * (Format, Land); Schritt 2 prueft **dieselbe Datei** gegen XSD und
 * Regelwerk (KW-003) und sagt, nach welchen Regelwerken. Je Schritt eine
 * Einheit — der Ablagehinweis nennt beide.
 *
 * ⚪ Am 2026-10-03 rief die Seite kurz einen eigenen Endpunkt
 * (`/validate-document`), der das Original geprueft haette. Er war ein
 * vierter Validierungspfad neben drei vorhandenen und wurde zurueckgebaut.
 *
 * Datei rein, Urteil raus: weder Land noch Format fragt die Seite ab. Erst
 * wenn die Erkennung nichts findet, bietet sie beides von Hand an.
 *
 * ⚪ App-lokal statt im geteilten Package: Diese Flaeche ist
 * invoice-api-eigen (Stand 2026-10-10 pinnt die App `invoice-frontend` auf
 * der aktuellen 3.52.0 — ein Rueckstand ist nicht mehr der Grund). Die Bausteine (`CountrySelector`, `ApiRequestInspector`) kommen
 * trotzdem aus dem Package, damit niemand zwei Bedienmuster lernt.
 */

/** Was die Ablageflaeche annimmt. Alles andere ist keine E-Rechnung. */
const ALLOWED_EXTENSIONS = ['.xml', '.pdf']

export interface SelectedFile {
  name: string
  size: number
  base64: string
}

export interface InvoiceValidationPageProps {
  catalog: CatalogCountry[]
  allowedCountries?: string[]
  requestState?: RestApiCallState | null
  apiKey?: string | null
  maskedApiKey?: string | null
  /** Der Aufruf selbst — eingespritzt, damit die Seite ohne Netz erzaehlbar bleibt. */
  onValidate: (args: {
    fileBase64: string
    fileName: string
    countryCode?: string
    format?: string
  }) => Promise<ValidationResult>
  /** Nur fuer Stories: ein bereits vorliegendes Ergebnis. */
  initialResult?: ValidationResult | null
  /** Nur fuer Stories: eine bereits gewaehlte Datei. */
  initialFile?: SelectedFile | null
}

/** Base64 aus einer Datei — ohne den `data:`-Vorspann des FileReaders. */
/** Base64 zurueck zu Text — UTF-8, wie E-Rechnungs-XML kodiert ist. Kaputte Bytes werden `\uFFFD`. */
function toText(base64: string): string {
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return new TextDecoder('utf-8').decode(bytes)
}

/** Die Rechnungsnummer und Positionszahl aus dem geparsten Objekt — nur, wenn sie da sind. */
function invoiceKeysOf(parsed: unknown): { invoiceNumber?: string; itemCount?: number } {
  if (!parsed || typeof parsed !== 'object') return {}
  const o = parsed as { invoiceNumber?: unknown; items?: unknown }
  return {
    ...(typeof o.invoiceNumber === 'string' && o.invoiceNumber
      ? { invoiceNumber: o.invoiceNumber }
      : {}),
    ...(Array.isArray(o.items) ? { itemCount: o.items.length } : {}),
  }
}

async function toBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer())
  let raw = ''
  for (let i = 0; i < buffer.length; i += 0x8000) {
    raw += String.fromCharCode(...buffer.subarray(i, i + 0x8000))
  }
  return btoa(raw)
}

export function InvoiceValidationPage({
  catalog,
  allowedCountries,
  requestState,
  apiKey,
  maskedApiKey,
  onValidate,
  initialResult = null,
  initialFile = null,
}: InvoiceValidationPageProps) {
  const { t, i18n } = useTranslation()
  // 🔴 Ein ZWEITES `t`, gebunden an `invoiceConversion`. Die Bausteine aus dem
  // Paket (`CountrySelector`, `ApiRequestInspector`) schlagen ihre Saetze in
  // DIESEM Namensraum nach — das Plugin registriert ihn mit den Buendeln aus
  // `@xhubio-saas/invoice-frontend`. Mit dem Standard-`t` findet i18next dort
  // nichts und zeigt die rohen Schluessel: `inspector.title`,
  // `inspector.tab.curl`, `country.selectPlaceholder` (im Browser gesehen
  // 2026-10-03). Meine eigenen Saetze liegen weiter in `common`.
  const { t: tPackage } = useTranslation('invoiceConversion')
  const [file, setFile] = useState<SelectedFile | null>(initialFile)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<ValidationResult | null>(initialResult)
  const [inputError, setInputError] = useState<string | null>(null)
  const [manual, setManual] = useState(false)
  const [country, setCountry] = useState<string | null>(null)
  const [format, setFormat] = useState<string>('')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const catalogCountry = useMemo(
    () => (country ? findCatalogCountry(catalog, country) : null),
    [catalog, country]
  )
  const eInvoiceFormats = useMemo(
    () => (catalogCountry?.formats ?? []).filter((f) => f.kind === 'einvoice'),
    [catalogCountry]
  )

  const outcome = useMemo(() => (result ? outcomeOf(result) : null), [result])

  /**
   * Der Anzeigename des Formats aus dem Katalog (`GET /formats`) — bei Erkennung wie bei Handauswahl
   * (`ZUGFeRD 2.3/2.4/2.5` statt `ZUGFERD_EN16931`). 🔴 Aus der API ueber dieselbe Zuordnung wie der Pruefpfad
   * (`catalogLabel` → `catalogToken`), nie aus einer Liste hier — aber nur, wenn die Erkennung das Profil belegt:
   * generisches `UBL`/`CII` heisst „UBL"/„CII", nicht wie der Pruefpfad (`formatDisplayName`). Kein Treffer:
   * `undefined`, die Karte zeigt das Token.
   */
  const formatLabel = useMemo(() => {
    const code = result?.detection?.countryCode
    if (!result?.detection || !code) return undefined
    return formatDisplayName(
      result.detection.format,
      result.selectedFormat,
      findCatalogCountry(catalog, code)?.formats ?? [],
      result.detection.formatVersion
    )
  }, [catalog, result])

  /** Gleiche Befunde (gleiche Kennung, gleicher Text — etwa aus zwei Regelwerken) als eine Zeile. */
  const groupedFindings = useMemo(() => groupFindings(result?.findings ?? []), [result])
  const groupedWarnings = useMemo(() => groupFindings(result?.warnings ?? []), [result])

  /**
   * Was der Nutzer hochgeladen hat, lesbar. 🔴 Ein PDF wird NICHT dekodiert: Binaerbytes als UTF-8
   * waeren Zeichensalat. Sein XML liest der Server; `/parse` liefert es nicht mit (nur den Hinweis
   * `EXTRACTED_FROM_PDF`) — dann sagt der Block genau das. Entschieden am Dateinamen und an der
   * `%PDF-`-Signatur, dieselbe Regel wie das Backend (`invoice.ts`, Schritt 6 von `/parse`).
   */
  const fileText = useMemo(() => {
    if (!file) return null
    const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.base64.startsWith('JVBERi0')
    return isPdf ? { isPdf: true as const } : { isPdf: false as const, text: toText(file.base64) }
  }, [file])

  /**
   * Die Kennung, die die Datei selbst traegt (`cbc:CustomizationID` bzw. CII-Guideline-Id) — aus dem schon
   * dekodierten Text. Beim PDF nichts: sein XML liegt nicht im Browser.
   */
  const fileIdentifier = useMemo(
    () => (fileText && !fileText.isPdf ? documentIdentifier(fileText.text) : undefined),
    [fileText]
  )

  const parsedText = useMemo(
    () => (result?.parsed !== undefined ? JSON.stringify(result.parsed, null, 2) : null),
    [result]
  )

  const validate = useCallback(
    async (selected: SelectedFile, countryCode?: string, formatCode?: string) => {
      setRunning(true)
      setResult(null)
      try {
        const runResult = await onValidate({
          fileBase64: selected.base64,
          fileName: selected.name,
          countryCode,
          format: formatCode,
        })
        setResult(runResult)
        // Die Erkennung hat nichts gefunden: Land und Format von Hand
        // anbieten, mit dem, was sie immerhin vermutet hat.
        if (runResult.abort?.kind === 'notDetected') {
          setManual(true)
          if (runResult.detection?.countryCode) setCountry(runResult.detection.countryCode)
        }
      } finally {
        setRunning(false)
      }
    },
    [onValidate]
  )

  const readFile = useCallback(
    async (selected: File) => {
      const name = selected.name.toLowerCase()
      // 🔴 Nur `.xml` und `.pdf`. Wer Rechnungsdaten ablegt, bekommt den Weg
      // genannt statt eines Urteils ueber etwas, das wir nicht geprueft haben.
      if (!ALLOWED_EXTENSIONS.some((e) => name.endsWith(e))) {
        setInputError(
          t(
            'invoiceValidation.nurDokument',
            'Geprüft werden fertige E-Rechnungen als XML oder PDF. Rechnungsdaten (JSON) werden unter Conversion zu einer E-Rechnung.'
          )
        )
        return
      }
      setInputError(null)
      setManual(false)
      setCountry(null)
      setFormat('')
      const prepared = {
        name: selected.name,
        size: selected.size,
        base64: await toBase64(selected),
      }
      setFile(prepared)
      // Auswählen genügt — das System sagt von selbst, was es ist.
      await validate(prepared)
    },
    [validate, t]
  )

  /**
   * Die Ablage-Handler, die sich beide Zustaende teilen.
   *
   * ⚪ Einmal definiert, zweimal gespreizt: die grosse Flaeche und die schmale
   * Zeile sind verschiedene Elemente (`<button>` bzw. `<div>` mit eigenem
   * Knopf), aber das Hineinziehen muss in beiden gleich funktionieren.
   */
  const dropHandlers = useMemo(
    () => ({
      onDragOver: (e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        setDragOver(true)
      },
      onDragLeave: () => setDragOver(false),
      onDrop: (e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        setDragOver(false)
        const selected = e.dataTransfer.files?.[0]
        if (selected) void readFile(selected)
      },
    }),
    [readFile]
  )

  const rerun = useCallback(() => {
    if (!file) return
    void validate(file, country ?? undefined, format || undefined)
  }, [country, file, format, validate])

  /**
   * Die Werkbank rechts: Datei · Geparst · Anfrage — je ein Tab, nur wenn es den Inhalt gibt. 🔴 Reihenfolge wie
   * bisher die Bloecke: was hinein ging, was herauskam, wie der Aufruf aussah.
   */
  const panels: WorkbenchPanel[] = []
  if (file && fileText) {
    panels.push({
      id: 'file',
      label: t('invoiceValidation.werkbank.datei', 'Datei'),
      hint: fileText.isPdf ? 'PDF' : 'XML',
      content: (
        <ContentBlock
          testId="invoice-validation-content"
          collapsible={false}
          title={t('invoiceValidation.content.title', 'Inhalt der Datei')}
          // Das Format steht NICHT hier: die Erkennungskarte nennt es einmal, mit Namen statt Token.
          chips={[
            { text: file.name },
            {
              text: t('invoiceValidation.content.groesse', {
                defaultValue: '{{kb}} KB',
                kb: Math.max(1, Math.round(file.size / 1024)),
              }),
            },
            fileText.isPdf
              ? undefined
              : (() => {
                  const n = fileText.text.split('\n').length
                  return {
                    text: t(`invoiceValidation.content.lines.${pluralCategory(i18n.language, n)}`, {
                      n,
                    }),
                  }
                })(),
          ]}
          content={fileText.isPdf ? undefined : fileText.text}
          hint={t(
            'invoiceValidation.content.pdfHinweis',
            'PDF — das eingebettete XML liest der Server, sein Inhalt wird nicht mitgeliefert.'
          )}
          language="xml"
          fileName={file.name}
          bytes={file.size}
        />
      ),
    })
  }
  if (result?.parsed !== undefined && parsedText !== null) {
    const k = invoiceKeysOf(result.parsed)
    panels.push({
      id: 'parsed',
      label: t('invoiceValidation.werkbank.geparst', 'Geparst'),
      hint: 'JSON',
      content: (
        <ContentBlock
          testId="invoice-validation-parsed"
          collapsible={false}
          title={t('invoiceValidation.parsed.title', 'Geparste Rechnung (JSON)')}
          chips={[
            // Die Rechnungsnummer ist eine Kennung — sie darf Mono bleiben.
            k.invoiceNumber ? { text: k.invoiceNumber, mono: true } : undefined,
            k.itemCount !== undefined
              ? {
                  text: t(
                    `invoiceValidation.parsed.items.${pluralCategory(i18n.language, k.itemCount)}`,
                    { n: k.itemCount }
                  ),
                }
              : undefined,
          ]}
          content={parsedText}
          language="json"
          fileName={`${file?.name.replace(/\.[^.]+$/, '') ?? 'invoice'}.json`}
          bytes={new Blob([parsedText]).size}
        />
      ),
    })
  }
  if (requestState) {
    panels.push({
      id: 'request',
      label: t('invoiceValidation.werkbank.anfrage', 'Anfrage'),
      content: (
        // 🔴 Uebergang (Hallmark-Audit 2026-10-10 v1.71.3, Punkt 3): Der Kopf des Inspectors (invoice-frontend) laeuft
        // bei 375 px ueber den Rand und machte die ganze Seite breiter. Die Huelle haelt ihn in der Spaltenbreite —
        // der Status-Chip wird bis zum Paket-Fix abgeschnitten, die Seite scrollt nicht mehr waagrecht.
        // Issue: REQUIREMENTS/API/issues/2026-10-10_api-inspector-kopf-mobil-und-leerraum.md
        <div
          // Weisse Flaeche auf der grauen Seite (Dashboard-Ruhe): die Huelle des Inspectors
          // (invoice-frontend) hat keinen eigenen Hintergrund.
          className="min-w-0 max-w-full overflow-hidden rounded-md bg-card"
          data-testid="invoice-validation-inspector"
        >
          <ApiRequestInspector
            request={requestState.lastRequest}
            response={requestState.lastResponse}
            apiKey={apiKey ?? ''}
            maskedApiKey={maskedApiKey ?? ''}
            t={tPackage as (key: string) => string}
          />
        </div>
      ),
    })
  }

  // Die Werkbank erst, wenn es etwas zu zeigen gibt — vorher steht die Ablageflaeche allein.
  const hasWorkbench = panels.length > 0

  return (
    <div className="container mx-auto space-y-6 px-4 py-6" data-testid="invoice-validation-page">
      <div>
        <h1 className="min-w-0 text-3xl tracking-tight [overflow-wrap:anywhere]">
          {t('invoiceValidation.title', 'Validation')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            'invoiceValidation.subtitle',
            'Eine fertige E-Rechnung prüfen — Datei auswählen, das System erkennt Format und Land und sagt, ob sie gültig ist.'
          )}
        </p>
      </div>

      {/* 🔴 Single Column Report (Hallmark-Redesign 2026-10-10, Patrick nach Sichtung): EINE Spalte bei jeder Breite,
          volle Seitenbreite, nichts klebt. Reihenfolge Datei → Erkennung → Urteil → Befunde je Regelwerk → Werkbank.
          ⚪ Das zuerst gebaute Diptychon (Urteil links 5/12 klebend, Werkbank rechts 7/12) wurde verworfen: die
          einspaltige Fassung liest sich als Bericht, das Diptychon zwang Befundtexte in eine schmale Spalte.
          `min-w-0`: ein Rasterkind waechst sonst mit seinem breitesten Inhalt (CodeViewer, lange Kennungen). */}
      <div className="grid grid-cols-1 gap-6" data-testid="invoice-validation-layout">
        <div className="min-w-0 space-y-4" data-testid="invoice-validation-verdict-column">
          {/* 🔴 Die Flaeche folgt dem Zustand — Hausprazedenz `LogoUpload` im geteilten core-frontend
              (`{value ? <schmale Zeile> : <Dropzone>}`). Ohne Datei ist die ganze Flaeche ein echtes `<button>`; mit
              Datei traegt die Zeile einen eigenen `<Button>` (Knopf in Knopf waere ungueltiges HTML). Das Ablegen
              bleibt in BEIDEN Zustaenden moeglich. */}
          {file ? (
            <div
              data-testid="invoice-validation-dropzone"
              className={`flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-2 ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border bg-card'
              }`}
              {...dropHandlers}
            >
              <FileUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-32 flex-1 truncate text-sm tabular-nums">
                {t('invoiceValidation.datei', {
                  defaultValue: '{{name}} · {{kb}} KB',
                  name: file.name,
                  kb: Math.max(1, Math.round(file.size / 1024)),
                })}
                {running ? ` · ${t('invoiceValidation.laeuft', 'Prüfe…')}` : ''}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto shrink-0 whitespace-nowrap"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('invoiceValidation.andereDatei', 'Andere Datei wählen')}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="invoice-validation-dropzone"
              className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors motion-reduce:transition-none ${
                dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 bg-card'
              }`}
              onClick={() => fileInputRef.current?.click()}
              {...dropHandlers}
            >
              <FileUp className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="font-medium">
                {t('invoiceValidation.drop.title', 'E-Rechnung hierher ziehen oder auswählen')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t(
                  'invoiceValidation.drop.hint',
                  '.xml · .pdf — ein Dokument kostet 2 Einheiten (Lesen und Prüfen)'
                )}
              </p>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,.pdf,application/xml,text/xml,application/pdf"
            className="hidden"
            data-testid="invoice-validation-file"
            onChange={(e) => {
              const selected = e.target.files?.[0]
              if (selected) void readFile(selected)
            }}
          />

          {inputError && (
            <p className="text-sm text-destructive" role="alert">
              {inputError}
            </p>
          )}

          {result?.detection && (
            <DetectionCard
              detection={result.detection}
              fromPdf={result.fromPdf}
              // Eine Datei, deren Lesen scheiterte, traegt keine Sicherheit der Erkennung — auch ohne Abbruch
              // (`/parse` 200 `success:false`, Issue API 2026-10-14 ia-fe-validierung-zeigt-sicherheit-bei-gescheitertem-lesen).
              confident={!result.abort && result.wasRead}
              // Eine Handwahl, die der Server als falsches Format abweist: die Karte nennt den gewaehlten Pfad, nicht
              // die Datei (Issue API 2026-10-09 validierungsseite-zeigt-nach-handwahl-das-format-des-falschen-pfads).
              manual={
                result.selectedFormat !== undefined && result.abort?.kind === 'formatMismatch'
              }
              rulebooks={result.rulebooks}
              formatLabel={formatLabel}
              identifier={fileIdentifier}
              onChangeSelection={() => setManual(true)}
            />
          )}

          {/* 🔴 Reihenfolge links: Datei → Erkennung → Urteil → (Abbruch, Handauswahl) → Befunde. Das Urteil steht direkt
              unter seiner Herkunft und vor jeder Erklaerung — ab `lg` ohne Scrollen sichtbar. */}
          {outcome && result && (
            <div className="space-y-3" data-testid="invoice-validation-verdict">
              <VerdictHeader
                outcome={outcome}
                findingCount={groupedFindings.length}
                warningCount={groupedWarnings.length}
                rulebooks={result.rulebooks}
                rulebookResults={result.rulebookResults}
                reason={result.reason}
              />
              {/* Was nicht lief. Wonach geprueft wurde, steht in der Erkennung — die Spec-Ids der Regelwerke tragen
                  Fassung und Profil (`xrechnung-3.0.2-ubl`). */}
              {result.skippedRulebooks && result.skippedRulebooks.length > 0 && (
                <ul
                  className="space-y-1 text-sm text-muted-foreground"
                  data-testid="invoice-validation-skipped"
                >
                  {result.skippedRulebooks.map((n) => (
                    <li key={`${n.rulebook ?? '?'}-${n.reason}`}>
                      {t('invoiceValidation.nichtGeprueftRegelwerk', {
                        defaultValue: 'Nicht geprüft: {{regelwerk}} ({{grund}})',
                        regelwerk: n.rulebook ?? '?',
                        grund: n.reason,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 🔴 Der Hinweis steht VOR der Handauswahl: er ist ihre Begruendung. */}
          {result?.abort && <AbortNotice abort={result.abort} />}

          {manual && (
            <Card>
              {/* 🔴 Kein `pb-3`: `Card` setzt bereits `gap-6` zwischen Kopf und Inhalt. */}
              <CardHeader>
                <CardTitle className="text-base">
                  {t('invoiceValidation.vonHand.title', 'Format und Land selbst wählen')}
                </CardTitle>
              </CardHeader>
              {/* 🔴 `items-start`, nicht `items-end`: `CountrySelector` und `Select` haengen je ein UNSICHTBARES
                  Eingabefeld unterschiedlicher Groesse unter ihren Knopf (gemessen 2026-10-03). */}
              <CardContent className="flex flex-wrap items-start gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t('invoiceValidation.country', 'Land')}
                  </Label>
                  <CountrySelector
                    value={(country ?? null) as never}
                    onChange={(c) => {
                      setCountry(c as string)
                      setFormat('')
                      // 🔴 Das Urteil gehoert zu der Eingabe, mit der es entstand. Wer Land oder Format aendert,
                      // sieht sonst „gueltig" zu einer Auswahl, die nie geprueft wurde.
                      setResult(null)
                    }}
                    t={tPackage as never}
                    countries={catalog}
                    allowedCountries={allowedCountries as never}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t('invoiceValidation.format', 'Format')}
                  </Label>
                  <Select
                    value={format}
                    onValueChange={(f) => {
                      setFormat(f)
                      // Siehe oben: geaenderte Eingabe, verworfenes Urteil.
                      setResult(null)
                    }}
                    disabled={!catalogCountry}
                  >
                    <SelectTrigger className="w-56 max-w-full">
                      <SelectValue
                        placeholder={t('invoiceValidation.formatWaehlen', 'Format wählen')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {eInvoiceFormats.map((f) => (
                        <SelectItem key={f.code} value={f.code}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              {/* 🔴 Die Aktion steht im Form-Footer, rechts (`DESIGN.md` §5). */}
              <CardFooter className="justify-end">
                <Button onClick={rerun} disabled={!file || !country || !format || running}>
                  {t('invoiceValidation.erneut', 'Erneut prüfen')}
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Der Befund-Report — je Regelwerk gruppiert, wenn die Antwort `results[]` traegt. */}
          {result && (
            <FindingsReport
              findings={result.findings}
              warnings={result.warnings}
              rulebookResults={result.rulebookResults}
            />
          )}
        </div>

        {hasWorkbench && (
          <div className="min-w-0" data-testid="invoice-validation-workbench-column">
            <Workbench panels={panels} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Was der Server aus den Bytes gelesen hat — sichtbar und korrigierbar.
 *
 * 🔴 Festes Layout (Hallmark-Audit 2026-10-10, Punkte 3/4/8):
 *  · Zeile 1 links: Formatname · Version · Land (· Sicherheit unter 100 %); rechts IMMER „Anderes wählen".
 *    Schmal (375 px, Fix 2026-10-10): passt der Block nicht neben den Knopf, steht der Knopf rechtsbuendig darunter;
 *    „· Version" ist ein nicht trennbares Paar, der Trenner verwaist nie am Zeilenende.
 *  · Darunter eine Definitionsliste (`<dl>`, Hallmark-Audit 2026-10-10 v1.71.1, Punkt 3): je Fakt eine Zeile —
 *    „Kennung" (aus der Datei, Mono) und „Geprüft nach" (Regelwerke). Beschriftungen in einer gemeinsamen Spalte
 *    (`max-content`), Werte umbrechend (`overflow-wrap:anywhere`). Unter `sm` (640 px) steht die Beschriftung
 *    ueber dem Wert. „XML aus dem PDF gelesen" ist kein Paar aus Beschriftung und Wert, sondern ein Satz: eigene
 *    Zeile unter der Liste.
 * Der Formatname steht EINMAL: der Name aus `formatDisplayName`, sonst das Token. Das Token selbst nur als `title`.
 *
 * 🔴 Die Karte behauptet ueber die Datei nur, was belegt ist (Patrick 2026-10-10): Bei generischem UBL/CII nennt sie
 * den generischen Namen, nicht das Label des Pruefpfads; die Kennung liest sie aus der Datei selbst, beim PDF gar
 * nicht (Inhalt nicht im Browser), und fehlt sie, fehlt die Angabe.
 *
 * ⚪ Keine eigene Schattenebene: dieselbe Huelle wie die Dateizeile direkt darueber (`rounded-lg border`, ohne
 * `shadow-sm` der `Card`). Beide sind Statuszeilen zur Datei, keine Inhaltskarten.
 *
 * ⚪ Die Sicherheit steht nur dabei, wenn sie unter 100 liegt: „100 % sicher" ist kein Satz, den jemand lesen muss.
 */
function DetectionCard({
  detection,
  fromPdf,
  confident,
  manual,
  rulebooks,
  formatLabel,
  identifier,
  onChangeSelection,
}: {
  detection: Detection
  fromPdf?: boolean
  confident: boolean
  /** Format und Land stammen aus der Handwahl, und der Server hat sie als falsch abgewiesen. */
  manual?: boolean
  /** Die Regelwerke aus `/validate` (`results[].format`) — Spec-Ids mit Fassung und Profil. */
  rulebooks?: string[]
  /** Das Katalog-Label des Formats (`GET /formats`); fehlt es, steht das Token. */
  formatLabel?: string
  /** Die Spezifikationskennung aus der Datei (`documentIdentifier`); fehlt sie, fehlt die Angabe. */
  identifier?: string
  onChangeSelection: () => void
}) {
  const { t } = useTranslation()
  const profile = formatProfile(detection.format)
  const rows: ({ id: string; label: string; value: ReactNode; mono?: boolean } | undefined)[] = [
    identifier
      ? {
          id: 'identifier',
          label: t('invoiceValidation.erkennung.kennung', 'Kennung'),
          value: identifier,
          mono: true,
        }
      : undefined,
    rulebooks && rulebooks.length > 0
      ? {
          id: 'rulebooks',
          label: t('invoiceValidation.erkennung.geprueftNach', 'Geprüft nach'),
          value: rulebooks.join(', '),
        }
      : undefined,
  ]
  const detailRows = rows.filter((r): r is NonNullable<(typeof rows)[number]> => r !== undefined)

  return (
    <section
      className="space-y-1 rounded-lg border border-border bg-card px-4 py-3"
      data-testid="invoice-validation-detection"
    >
      {/* Schmal (375 px): passt der Block nicht neben den Knopf, rutscht der Knopf als Ganzes unter den Block,
          rechtsbuendig (`ml-auto`) — der Block bricht nicht mehr um den Knopf herum. Breit bleibt alles in einer
          Zeile. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {/* 🔴 Der Name aus dem Katalog, das Token nur im Tooltip — kein Token + Label nebeneinander. */}
          <span
            className="font-medium"
            title={detection.format ?? undefined}
            data-testid="invoice-validation-format"
          >
            {detection.format
              ? (formatLabel ?? detection.format)
              : t('invoiceValidation.erkennung.unbekannt', 'Format unbekannt')}
          </span>
          {/* Das Profil aus dem Token der Erkennung (ZUGFeRD/Factur-X) — auch bei Handauswahl, es ist Eigenschaft der Datei.
              Trenner und Profil sind wie die Version EIN nicht trennbares Paar. */}
          {profile && (
            <span className="inline-flex items-center gap-2 whitespace-nowrap text-muted-foreground">
              <span aria-hidden="true">·</span>
              <span data-testid="invoice-validation-format-profile">{profile}</span>
            </span>
          )}
          {/* 🔴 Die Version nur aus `detection.formatVersion`. Fehlt sie, steht das ausdruecklich da — kein
              geratener Wert, kein Spektrum aus dem Label. */}
          {/* 🔴 Trenner und Version sind EIN nicht trennbares Paar: der „·" steht nie allein am Zeilenende. */}
          {detection.format && (
            <span
              className="inline-flex items-center gap-2 whitespace-nowrap text-muted-foreground"
              data-testid="invoice-validation-format-version-pair"
            >
              <span aria-hidden="true">·</span>
              <span className="tabular-nums" data-testid="invoice-validation-format-version">
                {detection.formatVersion
                  ? t('invoiceValidation.erkennung.version', {
                      defaultValue: 'Version {{version}}',
                      version: detection.formatVersion,
                    })
                  : t('invoiceValidation.erkennung.versionUnbekannt', 'Version unbekannt')}
              </span>
            </span>
          )}
          {detection.countryCode && <Badge variant="outline">{detection.countryCode}</Badge>}
          {manual && (
            <Badge variant="secondary" data-testid="invoice-validation-manual">
              {t('invoiceValidation.erkennung.vonHand', 'von Hand gewählt')}
            </Badge>
          )}
          {confident && detection.confidence < 100 && (
            <span className="whitespace-nowrap text-muted-foreground tabular-nums">
              {t('invoiceValidation.erkennung.sicherheit', {
                defaultValue: '{{n}} % sicher',
                n: Math.round(detection.confidence),
              })}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto shrink-0 whitespace-nowrap"
          onClick={onChangeSelection}
        >
          {t('invoiceValidation.erkennung.anderesWaehlen', 'Anderes wählen')}
        </Button>
      </div>
      {(detailRows.length > 0 || fromPdf) && (
        <div
          className="space-y-1 text-sm text-muted-foreground"
          data-testid="invoice-validation-detail"
        >
          {detailRows.length > 0 && (
            // Eine Spalte unter 640 px (Beschriftung ueber dem Wert), ab `sm` zwei: die Beschriftungen teilen
            // sich die Spalte, weil jede Zeile per `contents` direkt im Raster der Liste steht.
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[max-content_minmax(0,1fr)]">
              {detailRows.map((row) => (
                <div
                  key={row.id}
                  className="sm:contents"
                  data-testid={`invoice-validation-${row.id}`}
                >
                  <dt className="font-medium">{row.label}</dt>
                  <dd
                    className={`min-w-0 text-foreground [overflow-wrap:anywhere] ${row.mono ? 'font-mono text-xs leading-5' : ''}`}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {fromPdf && (
            <p data-testid="invoice-validation-pdf">
              {t('invoiceValidation.erkennung.ausPdf', 'XML aus dem PDF gelesen')}
            </p>
          )}
        </div>
      )}
    </section>
  )
}

function AbortNotice({ abort }: { abort: NonNullable<ValidationResult['abort']> }) {
  const { t } = useTranslation()
  const texts: Record<typeof abort.kind, string> = {
    notDetected: t(
      'invoiceValidation.abbruch.nichtErkannt',
      'Die Datei sagt nicht, welches Format sie hat. Format und Land unten selbst wählen.'
    ),
    quota: t(
      'invoiceValidation.abbruch.quota',
      'Das Kontingent ist erschöpft — es wurde nichts gelesen.'
    ),
    request: t('invoiceValidation.abbruch.request', 'Die Datei konnte nicht gelesen werden.'),
    // 🔴 Gelesen ja, geprueft nein. Der Nutzer muss wissen, dass die Haelfte
    // der Antwort fehlt — sonst haelt er „keine Befunde" fuer „in Ordnung".
    // Der Servertext darunter nennt beide Formate (Pfad und Datei).
    formatMismatch: t(
      'invoiceValidation.abbruch.formatAbweichung',
      'Die Datei hat ein anderes Format oder Land als der Prüfpfad. Es wurde nichts geprüft und nichts berechnet.'
    ),
    notValidatable: t(
      'invoiceValidation.abbruch.nichtPruefbar',
      'Die Datei wurde gelesen, aber für dieses Format und Land liegt uns kein Regelwerk vor. Es wurde nicht geprüft.'
    ),
    unavailable: t(
      'invoiceValidation.abbruch.unavailable',
      'Die Prüfung war nicht verfügbar. Es wurde nichts berechnet.'
    ),
    // 🔴 Eigener Satz. „Kein Schlüssel" und „Schlüssel ohne Recht" sind zwei
    // Lagen, und der Nutzer muss Verschiedenes tun: warten bzw. ein
    // Entitlement buchen. Seit dem Ausbau der tRPC-Tuer (2026-10-03) ist
    // „kein Schlüssel" ausserdem eine Sackgasse — der Satz muss das sagen,
    // statt einen Weg zu versprechen, den es nicht mehr gibt.
    noKey: t(
      'invoiceValidation.abbruch.keinSchluessel',
      'Es liegt kein gültiger Sitzungsschlüssel vor — das Stundenlimit ist erreicht. Bitte die Seite später neu laden.'
    ),
    auth: t(
      'invoiceValidation.abbruch.auth',
      'Der Schlüssel hat für dieses Format keine Berechtigung.'
    ),
    unknown: t('invoiceValidation.abbruch.unknown', 'Der Aufruf ist nicht durchgelaufen.'),
  }
  return (
    <Card className="border-destructive/40" data-testid="invoice-validation-abort">
      <CardContent className="space-y-1">
        <p className="font-medium">{texts[abort.kind]}</p>
        <p className="font-mono text-xs text-muted-foreground">{abort.message}</p>
      </CardContent>
    </Card>
  )
}
